/** Dual-track KPI cutover: v2 from June 2026, v1 archive before. */
export const KPI_V2_CUTOVER = { year: 2026, month: 6 }; // June 2026

/** Separate keys so archive (May) does not poison main KPI / details. */
export const KPI_STORAGE = {
  v2Year: "kpi_v2_year",
  v2Month: "kpi_v2_month",
  v1Year: "kpi_v1_year",
  v1Month: "kpi_v1_month",
};

const toNum = (value) => Number(value);

export const isV2Month = (year, month) => {
  const y = toNum(year);
  const m = toNum(month);
  return (
    y > KPI_V2_CUTOVER.year ||
    (y === KPI_V2_CUTOVER.year && m >= KPI_V2_CUTOVER.month)
  );
};

export const isV1Month = (year, month) => !isV2Month(year, month);

/** API prefix: pre-June → /crm/kpi/v1, June+ → /crm/kpi */
export const kpiApiBase = (year, month) =>
  isV1Month(year, month) ? "/crm/kpi/v1" : "/crm/kpi";

/** If before cutover, return June 2026; otherwise keep as numbers 1–12. */
export const clampToV2Month = (year, month) => {
  const y = toNum(year);
  const m = toNum(month);
  if (isV2Month(y, m)) {
    return { year: y, month: m };
  }
  return { year: KPI_V2_CUTOVER.year, month: KPI_V2_CUTOVER.month };
};

/** If on/after cutover, return May 2026; otherwise keep as numbers 1–12. */
export const clampToV1Month = (year, month) => {
  const y = toNum(year);
  const m = toNum(month);
  if (isV1Month(y, m)) {
    return { year: y, month: m };
  }
  return { year: KPI_V2_CUTOVER.year, month: KPI_V2_CUTOVER.month - 1 };
};

export const filterV2Years = (years) =>
  years.filter((el) => toNum(el.year) >= KPI_V2_CUTOVER.year);

export const filterV2Months = (months, year) => {
  const y = toNum(year);
  if (y > KPI_V2_CUTOVER.year) return months;
  if (y < KPI_V2_CUTOVER.year) return [];
  return months.filter((el) => toNum(el.id) >= KPI_V2_CUTOVER.month);
};

export const filterV1Years = (years) =>
  years.filter((el) => toNum(el.year) <= KPI_V2_CUTOVER.year);

export const filterV1Months = (months, year) => {
  const y = toNum(year);
  if (y < KPI_V2_CUTOVER.year) return months;
  if (y > KPI_V2_CUTOVER.year) return [];
  return months.filter((el) => toNum(el.id) < KPI_V2_CUTOVER.month);
};

export const readStoredV2Period = () => ({
  year: localStorage.getItem(KPI_STORAGE.v2Year),
  month: localStorage.getItem(KPI_STORAGE.v2Month),
});

export const writeStoredV2Period = (year, month) => {
  localStorage.setItem(KPI_STORAGE.v2Year, String(year));
  localStorage.setItem(KPI_STORAGE.v2Month, String(month));
};

export const readStoredV1Period = () => ({
  year: localStorage.getItem(KPI_STORAGE.v1Year),
  month: localStorage.getItem(KPI_STORAGE.v1Month),
});

export const writeStoredV1Period = (year, month) => {
  localStorage.setItem(KPI_STORAGE.v1Year, String(year));
  localStorage.setItem(KPI_STORAGE.v1Month, String(month));
};

/** Build hunter details URL keeping the report period. */
export const hunterDetailsPath = (employeeId, year, month) =>
  `/hunter/${employeeId}?year=${year}&month=${month}`;

/** Parse plan month without Date() TZ shift (date-only / ISO midnight UTC). */
export const monthFromPlanDate = (sdPlanMonth) => {
  if (!sdPlanMonth) return null;
  const text = String(sdPlanMonth);
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Number(m[2]);
  const d = new Date(sdPlanMonth);
  if (Number.isNaN(d.getTime())) return null;
  return d.getUTCMonth() + 1;
};

export const yearFromPlanDate = (sdPlanMonth) => {
  if (!sdPlanMonth) return null;
  const text = String(sdPlanMonth);
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Number(m[1]);
  const d = new Date(sdPlanMonth);
  if (Number.isNaN(d.getTime())) return null;
  return d.getUTCFullYear();
};
